import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTA } from "@/components/CTA";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

import shopifyLogo from "@/assets/partners/shopify.svg";
import klaviyoLogo from "@/assets/partners/klaviyo.svg";
import stripeLogo from "@/assets/partners/stripe.svg";
import mailchimpLogo from "@/assets/partners/mailchimp.svg";
import hubspotLogo from "@/assets/partners/hubspot.svg";
import wooLogo from "@/assets/partners/woocommerce.svg";
import weglotLogo from "@/assets/partners/weglot.svg";
import googleWorkspaceLogo from "@/assets/partners/google-workspace.svg";
import slackLogo from "@/assets/partners/slack.svg";
import notionLogo from "@/assets/partners/notion.svg";
import zohoLogo from "@/assets/partners/zoho.svg";
import openaiLogo from "@/assets/partners/openai.svg";
import hotjarLogo from "@/assets/partners/hotjar.svg";
import typeformLogo from "@/assets/partners/typeform.svg";

const integrations = [
  {
    name: "Shopify",
    category: "Ecommerce Platform",
    logo: shopifyLogo,
    description:
      "The world's leading ecommerce platform for building, launching, and scaling online stores.",
  },
  {
    name: "Shopify Plus",
    category: "Enterprise",
    logo: shopifyLogo,
    description:
      "Enterprise-grade Shopify with advanced automation, custom checkout, and scalable architecture.",
  },
  {
    name: "WooCommerce",
    category: "Ecommerce Platform",
    logo: wooLogo,
    description:
      "Flexible, open-source ecommerce platform built on WordPress for fully customisable storefronts.",
  },
  {
    name: "Stripe",
    category: "Payments",
    logo: stripeLogo,
    description:
      "Secure, flexible payment processing for online and in-person transactions worldwide.",
  },
  {
    name: "Klaviyo",
    category: "Email & SMS",
    logo: klaviyoLogo,
    description:
      "Email and SMS marketing automation designed specifically for ecommerce brands.",
  },
  {
    name: "Mailchimp",
    category: "Email Marketing",
    logo: mailchimpLogo,
    description:
      "All-in-one marketing platform for automated email campaigns, audiences, and journeys.",
  },
  {
    name: "HubSpot",
    category: "CRM",
    logo: hubspotLogo,
    description:
      "CRM and marketing platform to manage customer relationships and drive growth.",
  },
  {
    name: "Zoho",
    category: "CRM & Operations",
    logo: zohoLogo,
    description:
      "Complete business suite covering CRM, inventory, books, and customer support.",
  },
  {
    name: "Hotjar",
    category: "Analytics",
    logo: hotjarLogo,
    description:
      "Heatmaps, recordings, and feedback to understand on-site behaviour and lift conversions.",
  },
  {
    name: "Typeform",
    category: "Forms & Surveys",
    logo: typeformLogo,
    description:
      "Conversational forms and quizzes for product finders, lead capture, and post-purchase surveys.",
  },
  {
    name: "Weglot",
    category: "Localization",
    logo: weglotLogo,
    description:
      "Translate your store into Arabic and any language with SEO-friendly multilingual delivery.",
  },
  {
    name: "Google Workspace",
    category: "Productivity",
    logo: googleWorkspaceLogo,
    description:
      "Business email, Drive, and collaboration suite to run your store operations end-to-end.",
  },
  {
    name: "Slack",
    category: "Communication",
    logo: slackLogo,
    description:
      "Real-time alerts for orders, abandoned carts, and inventory directly to your team channels.",
  },
  {
    name: "Notion",
    category: "Operations",
    logo: notionLogo,
    description:
      "Centralise SOPs, product launches, and content briefs across marketing and operations.",
  },
  {
    name: "OpenAI",
    category: "AI",
    logo: openaiLogo,
    description:
      "Generative AI for product descriptions, on-site search, and customer support automation.",
  },
  {
    name: "Meta Pixel & Conversions API",
    category: "Advertising",
    description:
      "Track conversions across Facebook and Instagram with server-side accuracy for iOS users.",
  },
  {
    name: "Google Analytics 4",
    category: "Analytics",
    description:
      "Event-based analytics with ecommerce reports, funnels, and audience insights.",
  },
  {
    name: "Google Ads",
    category: "Advertising",
    description:
      "Performance Max, Shopping, and Search campaigns with full product feed sync.",
  },
  {
    name: "TikTok Shop",
    category: "Social Commerce",
    description:
      "Sell directly inside TikTok with native checkout, live shopping, and creator partnerships.",
  },
  {
    name: "Aramex & DHL",
    category: "Logistics",
    description:
      "Regional and global shipping integrations with rates, label printing, and tracking sync.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: Math.min(i, 10) * 0.05, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const EcommerceIntegrations = () => {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const { canonical, hreflang } = buildHreflang("/ecommerce/integrations-directory");

  useCmsPageSEO({
    title:
      lang === "ar"
        ? "تكاملات التجارة الإلكترونية | وافاي"
        : "Ecommerce Integrations | Top 20 Apps & Tools | Wavai",
    description:
      lang === "ar"
        ? "أهم 20 تكاملاً للتجارة الإلكترونية يستخدمها وافاي لبناء وتنمية المتاجر: Shopify، Klaviyo، Stripe، HubSpot وغيرها."
        : "The top 20 ecommerce integrations Wavai uses to build and scale online stores — Shopify, Klaviyo, Stripe, HubSpot, Meta, Google, and more.",
    canonical,
    hreflang,
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 lg:pt-40 lg:pb-16">
          <div className="container px-6 max-w-5xl">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <Link
                to={localePath("/ecommerce")}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("Back to Ecommerce")}
              </Link>
              <span className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4">
                {t("Apps & Integrations")}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05] tracking-tight text-balance mb-5">
                {t("The tools we use to build and scale ecommerce")}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed text-balance">
                {t(
                  "A curated stack of the top 20 platforms we connect to your Shopify or WooCommerce store — from payments and email to analytics, advertising, and AI."
                )}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Grid */}
        <section className="pb-20 lg:pb-28">
          <div className="container px-6 max-w-6xl">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {integrations.map((item, i) => (
                <motion.article
                  key={item.name}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-card rounded-2xl p-6 border border-border shadow-card hover:shadow-glow hover:border-primary/25 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl border border-border bg-background flex items-center justify-center shrink-0">
                      {item.logo ? (
                        <img
                          src={item.logo}
                          alt={item.name}
                          className="h-7 w-7 object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-base font-display font-bold text-primary">
                          {item.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-sans font-medium text-lg leading-tight mb-0.5 truncate">
                        {item.name}
                      </h2>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        {t(item.category)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(item.description)}
                  </p>
                </motion.article>
              ))}
            </div>

            <div className="mt-16 text-center">
              <p className="text-base text-muted-foreground mb-4">
                {t("Need a tool that's not on this list?")}
              </p>
              <Link
                to={localePath("/contact")}
                className="inline-flex items-center gap-1.5 text-base text-primary font-medium hover:underline"
              >
                {t("Talk to us about your stack")}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
};

export default EcommerceIntegrations;
